import { useEffect } from 'react';

import { getAaId } from '@trello/authentication';
import { sendErrorEvent } from '@trello/error-reporting';
import { Poller } from '@trello/poller';

import { fetchUserPersonalization } from './fetchUserPersonalization';
import { isValidTraitName } from './traits';
import { userTraitsSharedState } from './userTraitsSharedState';

const BASE_REFRESH_INTERVAL_IN_MS_STG = 60_000; // 60 seconds
const BASE_REFRESH_INTERVAL_IN_MS_PROD = 3_600_000; // 1 hour

let poller: Poller;

const fetchUserTraits = async (aaId: string): Promise<void> => {
  if (!userTraitsSharedState.value[aaId]?.refreshing) {
    const previousTraitAttributes =
      userTraitsSharedState.value[aaId]?.attributes;
    const previousLoading = userTraitsSharedState.value[aaId]?.loading;

    userTraitsSharedState.setValue({
      ...userTraitsSharedState.value,
      [aaId]: {
        attributes: previousTraitAttributes ?? [],
        loading: previousLoading,
        refreshing: true,
      },
    });
    try {
      const { attributes } = await fetchUserPersonalization(aaId);
      if (!attributes) {
        userTraitsSharedState.setValue({
          ...userTraitsSharedState.value,
          [aaId]: {
            attributes: previousTraitAttributes ?? [],
            loading: false,
            refreshing: false,
          },
        });
        return;
      }

      const validAttributes = attributes.filter((attr) =>
        isValidTraitName(attr.name),
      );
      userTraitsSharedState.setValue({
        ...userTraitsSharedState.value,
        [aaId]: {
          attributes: validAttributes,
          loading: false,
          refreshing: false,
        },
      });
    } catch (error) {
      sendErrorEvent(error);
      userTraitsSharedState.setValue({
        ...userTraitsSharedState.value,
        [aaId]: {
          attributes: previousTraitAttributes ?? [],
          loading: false,
          refreshing: false,
        },
      });
      throw error;
    }
  }
};

export const useCachedUserTraits: () => void = () => {
  const userAaId = getAaId();

  useEffect(() => {
    if (!userAaId) {
      return;
    }

    if (!userTraitsSharedState.value[userAaId]) {
      userTraitsSharedState.setValue({
        ...userTraitsSharedState.value,
        [userAaId]: {
          attributes: [],
          loading: true,
          refreshing: false,
        },
      });
    }

    if (!poller) {
      poller = new Poller(
        async () => {
          await fetchUserTraits(userAaId);
        },
        {
          staging: BASE_REFRESH_INTERVAL_IN_MS_STG,
          prod: BASE_REFRESH_INTERVAL_IN_MS_PROD,
        },
      );
    }

    if (!poller.isPolling()) {
      poller.start();
    }
  }, [userAaId]);
};
